import { useProUser } from "@/contexts/pro-user-context";
import { createCheckoutSession } from "@/lib/payments";
import { track } from "@masterkey/analytics/client";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "./ui/use-toast";

import {
  PREMIUM_FEATURES,
  type PlanId,
  getPlusPlansByInterval,
  getProPlansByInterval,
} from "@/lib/constants/pricing";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export function SubscriptionPlansModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const { isPro } = useProUser();
  const plusPlans = getPlusPlansByInterval();
  const proPlans = getProPlansByInterval();
  const currentPlusPrice = isYearly ? plusPlans[1] : plusPlans[0];
  const currentProPrice = isYearly ? proPlans[1] : proPlans[0];

  async function handlePlanSelect(priceId: string, planId: PlanId) {
    try {
      setIsLoading(priceId);
      track({ event: "upgrade-intent", source: "subscription-plans-modal" });
      await createCheckoutSession({
        priceId,
        plan: planId,
        isTrial: false,
      });
      return;
    } catch (error) {
      console.error("Error during checkout:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
      setIsLoading(null);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden max-h-[90vh] w-full overflow-y-auto">
        <div className="bg-chalk p-4 sm:p-6 pb-4 sticky top-0 z-20">
          <DialogHeader>
            <DialogTitle className="text-center text-xl sm:text-2xl">
              {isPro ? "You're a Pro!" : "Choose Your Plan"}
            </DialogTitle>
            <p className="text-center text-sm text-darkGray">
              {isPro
                ? "You already have access to all premium features"
                : "Select the plan that best fits your home buying journey"}
            </p>
          </DialogHeader>

          {!isPro && (
            <div className="mt-4 flex justify-center">
              <div className="inline-flex p-1 bg-white rounded-full shadow-lg">
                <motion.div
                  className="absolute bg-canary h-9 rounded-full"
                  initial={false}
                  animate={{
                    x: isYearly ? "100%" : "0%",
                    width: "92px",
                  }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
                <button
                  onClick={() => setIsYearly(false)}
                  className={`relative px-6 py-2 rounded-full text-sm font-semibold z-10 transition-colors duration-200 ${
                    !isYearly ? "text-smokeyBlack" : "text-darkGray"
                  }`}
                  type="button"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsYearly(true)}
                  className={`relative px-6 py-2 rounded-full text-sm font-semibold z-10 transition-colors duration-200 ${
                    isYearly ? "text-smokeyBlack" : "text-darkGray"
                  }`}
                  type="button"
                >
                  Yearly
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs bg-smokeyBlack text-white rounded-full">
                    Save 17%
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Plus Plan */}
            <div className="rounded-xl bg-white p-4 border border-plaster/20">
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold">Plus</h3>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={isYearly ? "yearly-plus" : "monthly-plus"}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="text-2xl font-bold mt-2">
                      ${currentPlusPrice.monthlyPrice}
                      <span className="text-sm font-normal text-darkGray">
                        /mo
                      </span>
                    </div>
                    <div className="text-sm text-darkGray">
                      {currentPlusPrice.description}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              <Button
                onClick={() =>
                  handlePlanSelect(
                    currentPlusPrice.priceId,
                    isYearly ? "plus_yearly" : "plus_monthly",
                  )
                }
                variant="outline"
                className="w-full mb-4 border-canary hover:bg-canary/10"
                disabled={isLoading !== null || isPro}
              >
                {isLoading === currentPlusPrice.priceId ? (
                  "Processing..."
                ) : (
                  <>Upgrade to Plus</>
                )}
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="rounded-xl bg-smokeyBlack p-4 relative">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 bg-canary text-xs font-semibold rounded-full">
                  Most Popular
                </span>
              </div>
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-white">Pro</h3>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={isYearly ? "yearly-pro" : "monthly-pro"}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="text-2xl font-bold mt-2 text-white">
                      ${currentProPrice.monthlyPrice}
                      <span className="text-sm font-normal text-gray-400">
                        /mo
                      </span>
                    </div>
                    <div className="text-sm text-gray-400">
                      {currentProPrice.description}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              <Button
                onClick={() =>
                  handlePlanSelect(
                    currentProPrice.priceId,
                    isYearly ? "pro_yearly" : "pro_monthly",
                  )
                }
                className="w-full mb-4 bg-canary text-smokeyBlack hover:bg-canary/90"
                disabled={isLoading !== null || isPro}
              >
                {isLoading === currentProPrice.priceId ? (
                  "Processing..."
                ) : (
                  <>Upgrade to Pro</>
                )}
              </Button>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mb-4">
            <div className="text-xs text-darkGray flex items-center gap-2">
              <motion.div
                animate={{ y: [0, 3, 0] }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
                className="h-4 w-4"
              >
                ↓
              </motion.div>
              Scroll to compare features
            </div>
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-[1fr_repeat(2,auto)] gap-2 sm:gap-4 mb-4 px-2 sticky top-[73px] sm:top-0 bg-white z-10 py-2 border-b">
            <div className="text-sm font-semibold text-darkGray">Features</div>
            <div className="w-20 sm:w-28 text-center text-sm font-semibold text-darkGray">
              Plus
            </div>
            <div className="w-20 sm:w-28 text-center text-sm font-semibold text-darkGray">
              Pro
            </div>
          </div>

          {/* Features Comparison */}
          <div className="space-y-6 overflow-y-auto">
            {PREMIUM_FEATURES.map((group) => (
              <div key={group.title}>
                <h4 className="font-semibold mb-3 text-sm">{group.title}</h4>
                <div className="space-y-3">
                  {group.features.map((feature) => (
                    <div
                      key={feature.name}
                      className="grid grid-cols-[1fr_repeat(2,auto)] gap-2 sm:gap-4 items-center py-2 hover:bg-chalk/50 rounded-lg px-2 -mx-2 transition-colors"
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div
                          className={cn(
                            "h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0",
                            "bg-canary/10",
                          )}
                        >
                          <feature.icon className="h-3 w-3 text-smokeyBlack" />
                        </div>
                        <div>
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            {feature.name}
                            {feature.comingSoon && (
                              <span className="inline-flex items-center px-2 py-0.5 text-[10px] bg-chalk rounded-full text-gray-600 font-medium tracking-wide">
                                Coming Soon
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-darkGray">
                            {feature.detail}
                          </div>
                        </div>
                      </div>
                      <div className="w-20 sm:w-28 text-center">
                        {typeof feature.plus === "string" ? (
                          <span className="text-xs text-darkGray">
                            {feature.plus}
                          </span>
                        ) : feature.plus ? (
                          <Check className="h-4 w-4 text-plaster mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-lightGray mx-auto" />
                        )}
                      </div>
                      <div className="w-20 sm:w-28 text-center">
                        {typeof feature.pro === "string" ? (
                          <span className="text-xs text-darkGray">
                            {feature.pro}
                          </span>
                        ) : feature.pro ? (
                          <div className="h-4 w-4 rounded-full bg-canary flex items-center justify-center mx-auto">
                            <Check className="h-3 w-3 text-smokeyBlack" />
                          </div>
                        ) : (
                          <X className="h-4 w-4 text-lightGray mx-auto" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-4 sm:px-6 py-4 border-t text-center">
          <div className="text-xs text-darkGray">
            Cancel anytime • 30-day money-back guarantee
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
